// src/features/reit-info/api.ts
//
// Data fetcher for the REIT Info deep-dive page. Mirrors etf-info/api.ts and
// stock-info/api.ts: talks to the FastAPI reit_info router via the shared
// `apiClient` axios instance, which attaches the WordPress-issued JWT and
// bakes the `/api/v1` version prefix into its baseURL. We therefore request
// the bare `/reit-info/*` path here.
//
// REITs reuse the stock equity endpoints (FMP has no dedicated /stable/reit/
// namespace) but the backend extracts a Real-Estate-native slice: dividend
// yield, payout ratio, Price/FFO proxy, leverage & coverage, plus a dividend
// payout track record.
//
// CRITICAL: the backend serializes with `model_dump(by_alias=False,
// exclude_none=True)` -- so JSON keys are SNAKE_CASE and every field is
// optional (null fields are dropped).

import { apiClient } from '../../api/client';

const BASE_URL = '/reit-info';

// ---------------------------------------------------------------------------
// Island 1 — Overview (quote + profile + TTM yield ratios + Price/FFO)
// ---------------------------------------------------------------------------
export interface ReitOverview {
    symbol: string;
    quote: {
        symbol?: string;
        price?: number;
        changes_percentage?: number;
        change?: number;
        market_cap?: number;
        exchange?: string;
        name?: string;
    };
    profile: {
        company_name?: string;
        sector?: string;
        industry?: string;
        description?: string;
        beta?: number;
    };
    yield_metrics: {
        // `dividend_yield` and `payout_ratio` arrive as FRACTIONS (e.g. 0.0512
        // = 5.12%); the UI multiplies by 100 for display.
        dividend_yield?: number;
        payout_ratio?: number;
        price_to_book?: number;
        price_to_ocf?: number;
    };
    // Server-computed Price/FFO proxy (Price / Operating Cash Flow). Always
    // present in the payload but may itself be null when P/OCF is missing.
    price_to_ffo?: number | null;
}

export const fetchReitOverview = async (symbol: string): Promise<ReitOverview> => {
    const { data } = await apiClient.get<ReitOverview>(`${BASE_URL}/${symbol}/overview`);
    return data;
};

// ---------------------------------------------------------------------------
// Island 2 — Dividend history (payout track record)
// ---------------------------------------------------------------------------
export interface ReitDividend {
    date?: string;
    // Split-adjusted dividend amount.
    dividend?: number;
    payment_date?: string;
    declaration_date?: string;
    frequency?: string;
}

export const fetchReitDividends = async (
    symbol: string,
    limit = 60,
): Promise<ReitDividend[]> => {
    const { data } = await apiClient.get<ReitDividend[]>(
        `${BASE_URL}/${symbol}/dividends`,
        { params: { limit } },
    );
    return data;
};

// ---------------------------------------------------------------------------
// Island 3 — Leverage & safety metrics
// ---------------------------------------------------------------------------
export interface ReitSafetyMetrics {
    date?: string;
    period?: string;
    debt_to_equity?: number;
    debt_to_assets?: number;
    interest_coverage?: number;
    // Operating cash flow per share — the FFO/share proxy.
    ocf_per_share?: number;
    // FFO Payout Ratio = Dividends Paid / FFO (FFO = Net Income + Depreciation).
    // Arrives as a FRACTION (0.85 = 85%). Above 1.0 (>100%) is a red flag.
    ffo_payout_ratio?: number;
}

export const fetchReitSafetyMetrics = async (
    symbol: string,
): Promise<ReitSafetyMetrics> => {
    const { data } = await apiClient.get<ReitSafetyMetrics>(
        `${BASE_URL}/${symbol}/safety-metrics`,
    );
    return data;
};

// ---------------------------------------------------------------------------
// Island 4a — Intraday chart + technical indicators (identical to stock lens)
// ---------------------------------------------------------------------------
export interface ChartBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

export interface TechnicalIndicatorBar {
    date: string;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    // The active indicator arrives under its own key (e.g. `sma`, `rsi`).
    [indicator: string]: number | string;
}

export type TechnicalIndicator = 'sma' | 'ema' | 'wma' | 'rsi' | 'adx';

export const fetchIntradayChart = async (
    symbol: string,
    interval: string,
): Promise<ChartBar[]> => {
    const { data } = await apiClient.get<ChartBar[]>(`${BASE_URL}/${symbol}/chart`, {
        params: { interval },
    });
    return data;
};

export const fetchTechnicalIndicator = async (
    symbol: string,
    params: {
        indicator: TechnicalIndicator;
        periodLength: number;
        timeframe: string;
        from?: string;
        to?: string;
    },
): Promise<TechnicalIndicatorBar[]> => {
    const { data } = await apiClient.get<TechnicalIndicatorBar[]>(
        `${BASE_URL}/${symbol}/technical-indicators`,
        {
            params: {
                indicator: params.indicator,
                periodLength: params.periodLength,
                timeframe: params.timeframe,
                ...(params.from ? { from: params.from } : {}),
                ...(params.to ? { to: params.to } : {}),
            },
        },
    );
    return data;
};

// ---------------------------------------------------------------------------
// Island 4b — Peers (competitor REITs ranked by market cap)
// ---------------------------------------------------------------------------
export interface ReitPeer {
    symbol: string;
    name?: string;
    price?: number;
    changes_percentage?: number;
    market_cap?: number;
}

export const fetchReitPeers = async (symbol: string): Promise<ReitPeer[]> => {
    const { data } = await apiClient.get<ReitPeer[]>(`${BASE_URL}/${symbol}/peers`);
    return data;
};
